import { useState, useEffect } from 'react';
import { Button, Card } from './components';
import { t, LANGUAGES } from './lib/i18n';
import { useLang } from './store';
import './landing.css';

function Navbar() {
  const { lang } = useLang();
  const [scrolled, setScrolled] = useState(false);

  const NAV_LINKS = [
    { label: t('nav.features', lang), href: '#features' },
    { label: t('nav.howItWorks', lang), href: '#how-it-works' },
    { label: t('nav.pricing', lang), href: '#pricing' },
    { label: t('nav.customers', lang), href: '#customers' },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container navbar__inner">
        <a href="/" className="navbar__logo" aria-label="Look2it">
          <img
            src={scrolled ? '/logo-wordmark.svg' : '/logo-wordmark-neg.svg'}
            alt="Look2it"
            className="navbar__logo-img"
          />
        </a>
        <div className="navbar__links">
          {NAV_LINKS.map(({ label, href }) => (
            <a key={href} href={href} className="navbar__link">
              {label}
            </a>
          ))}
        </div>
        <div className="navbar__actions">
          <a href="/login" className="navbar__link">
            {t('nav.login', lang)}
          </a>
          <Button as="a" href="/signup" variant="brand" size="sm">
            {t('nav.getStarted', lang)}
          </Button>
        </div>
      </div>
    </nav>
  );
}

function DashboardMockup() {
  const { lang } = useLang();

  const DASHBOARD_ROWS = [
    {
      name: 'HQ — Floor 1 & 2',
      date: 'Inspected Mar 15',
      status: 'ok',
      label: t('dash.compliant', lang),
    },
    {
      name: 'Warehouse B',
      date: 'Due in 3 days',
      status: 'due',
      label: t('dash.dueSoon', lang),
    },
    {
      name: 'Branch Office 3',
      date: 'Overdue 12 days',
      status: 'overdue',
      label: t('dash.overdue', lang),
    },
    {
      name: 'Data Center — Level 1',
      date: 'Inspected Apr 1',
      status: 'ok',
      label: t('dash.compliant', lang),
    },
  ];

  return (
    <div
      className="dashboard-mockup"
      role="img"
      aria-label="Look2it dashboard preview"
    >
      <div className="dashboard-topbar">
        <span className="dot dot-red" aria-hidden="true" />
        <span className="dot dot-yellow" aria-hidden="true" />
        <span className="dot dot-green" aria-hidden="true" />
      </div>
      <div className="dashboard-body">
        <div className="dash-stat-row">
          <div className="dash-stat">
            <div className="dash-stat__label">{t('dash.compliant', lang)}</div>
            <div className="dash-stat__value dash-stat__value--green">94%</div>
          </div>
          <div className="dash-stat">
            <div className="dash-stat__label">{t('dash.dueSoon', lang)}</div>
            <div className="dash-stat__value dash-stat__value--yellow">8</div>
          </div>
          <div className="dash-stat">
            <div className="dash-stat__label">{t('dash.overdue', lang)}</div>
            <div className="dash-stat__value dash-stat__value--red">2</div>
          </div>
        </div>
        <div className="dash-table">
          {DASHBOARD_ROWS.map(row => (
            <div key={row.name} className="dash-row">
              <span className="dash-row__name">{row.name}</span>
              <span className="dash-row__date">{row.date}</span>
              <span className={`status-badge status-badge--${row.status}`}>
                {row.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const { lang } = useLang();

  return (
    <section className="hero" id="top">
      <div className="hero__grid" aria-hidden="true" />
      <div className="container">
        <div className="hero__content">
          <div>
            <div className="hero__badge">
              <span className="hero__badge-dot" aria-hidden="true" />
              {t('hero.badge', lang)}
            </div>
            <h1 className="hero__title">
              {t('hero.title1', lang)}
              <br />
              <span className="hero__title-accent">
                {t('hero.title2', lang)}
              </span>
            </h1>
            <p className="hero__sub">{t('hero.sub', lang)}</p>
            <div className="hero__actions">
              <Button as="a" href="/signup" variant="brand" size="lg">
                {t('hero.startTrial', lang)}
              </Button>
              <a
                href="#how-it-works"
                className="btn btn--outline-white btn--lg"
              >
                {t('hero.seeHow', lang)}
              </a>
            </div>
            <div className="hero__social-proof">
              <div className="hero__stat">
                <span className="hero__stat-value">2,400+</span>
                <span className="hero__stat-label">
                  {t('hero.stat.locations', lang)}
                </span>
              </div>
              <div className="hero__stat-divider" aria-hidden="true" />
              <div className="hero__stat">
                <span className="hero__stat-value">99.8%</span>
                <span className="hero__stat-label">
                  {t('hero.stat.compliance', lang)}
                </span>
              </div>
              <div className="hero__stat-divider" aria-hidden="true" />
              <div className="hero__stat">
                <span className="hero__stat-value">&lt; 1 hr</span>
                <span className="hero__stat-label">
                  {t('hero.stat.setup', lang)}
                </span>
              </div>
            </div>
          </div>
          <div className="hero__visual">
            <DashboardMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

const TRUST_LOGOS = [
  'Meridian Properties',
  'Clearview Manufacturing',
  'Sunrise Hospitality',
  'PacificEdge Realty',
  'NorthStar Logistics',
  'Summit Healthcare',
];

function TrustBar() {
  const { lang } = useLang();

  return (
    <div className="trust-bar" aria-label="Trusted by">
      <div className="container">
        <div className="trust-bar__inner">
          <span className="trust-bar__label">{t('trust.label', lang)}</span>
          <div className="trust-bar__logos" role="list">
            {TRUST_LOGOS.map(logo => (
              <span key={logo} className="trust-bar__logo" role="listitem">
                {logo}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PainPoints() {
  const { lang } = useLang();

  const cards = [
    {
      icon: '📋',
      title: t('pain.card1.title', lang),
      body: t('pain.card1.body', lang),
    },
    {
      icon: '📊',
      title: t('pain.card2.title', lang),
      body: t('pain.card2.body', lang),
    },
    {
      icon: '😰',
      title: t('pain.card3.title', lang),
      body: t('pain.card3.body', lang),
    },
  ];

  return (
    <section className="section section--alt" id="pain-points">
      <div className="container">
        <div className="text-center">
          <span className="section-tag">{t('pain.tag', lang)}</span>
          <h2 className="section-title">{t('pain.title', lang)}</h2>
          <p className="section-subtitle">{t('pain.sub', lang)}</p>
        </div>
        <div className="pain__grid">
          {cards.map(p => (
            <Card key={p.title} className="pain-card">
              <span className="pain-card__icon" aria-hidden="true">
                {p.icon}
              </span>
              <h3 className="pain-card__title">{p.title}</h3>
              <p className="pain-card__body">{p.body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const { lang } = useLang();

  const features = [
    {
      icon: '🗂️',
      title: t('features.registry.title', lang),
      body: t('features.registry.body', lang),
    },
    {
      icon: '⏰',
      title: t('features.reminders.title', lang),
      body: t('features.reminders.body', lang),
    },
    {
      icon: '📄',
      title: t('features.reports.title', lang),
      body: t('features.reports.body', lang),
    },
    {
      icon: '🏢',
      title: t('features.dashboard.title', lang),
      body: t('features.dashboard.body', lang),
    },
    {
      icon: '📱',
      title: t('features.mobile.title', lang),
      body: t('features.mobile.body', lang),
    },
    {
      icon: '📈',
      title: t('features.score.title', lang),
      body: t('features.score.body', lang),
    },
  ];

  return (
    <section className="section" id="features">
      <div className="container">
        <div className="text-center">
          <span className="section-tag">{t('features.tag', lang)}</span>
          <h2 className="section-title">{t('features.title', lang)}</h2>
          <p className="section-subtitle">{t('features.sub', lang)}</p>
        </div>
        <div className="features__grid">
          {features.map(f => (
            <Card key={f.title} className="feature-card">
              <div className="feature-card__icon" aria-hidden="true">
                {f.icon}
              </div>
              <h3 className="feature-card__title">{f.title}</h3>
              <p className="feature-card__body">{f.body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { lang } = useLang();

  const steps = [
    {
      num: '1',
      title: t('how.step1.title', lang),
      body: t('how.step1.body', lang),
    },
    {
      num: '2',
      title: t('how.step2.title', lang),
      body: t('how.step2.body', lang),
    },
    {
      num: '3',
      title: t('how.step3.title', lang),
      body: t('how.step3.body', lang),
    },
  ];

  return (
    <section className="section section--alt" id="how-it-works">
      <div className="container">
        <div className="text-center">
          <span className="section-tag">{t('how.tag', lang)}</span>
          <h2 className="section-title">{t('how.title', lang)}</h2>
          <p className="section-subtitle">{t('how.sub', lang)}</p>
        </div>
        <div className="how__steps">
          <div className="how__connector" aria-hidden="true" />
          {steps.map(s => (
            <div key={s.num} className="how-step">
              <div className="how-step__num" aria-hidden="true">
                {s.num}
              </div>
              <h3 className="how-step__title">{s.title}</h3>
              <p className="how-step__body">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

type CellValue = boolean | 'partial';

function Cell({ val }: { val: CellValue }) {
  if (val === true)
    return (
      <span className="check" aria-label="Yes">
        ✓
      </span>
    );
  if (val === 'partial')
    return (
      <span className="partial" aria-label="Partial">
        ~
      </span>
    );
  return (
    <span className="cross" aria-label="No">
      ✗
    </span>
  );
}

function Comparison() {
  const { lang } = useLang();

  const rows: {
    feature: string;
    ours: CellValue;
    spreadsheet: CellValue;
    contractor: CellValue;
  }[] = [
    {
      feature: t('compare.row.owners', lang),
      ours: true,
      spreadsheet: false,
      contractor: false,
    },
    {
      feature: t('compare.row.dashboard', lang),
      ours: true,
      spreadsheet: false,
      contractor: false,
    },
    {
      feature: t('compare.row.reminders', lang),
      ours: true,
      spreadsheet: false,
      contractor: 'partial',
    },
    {
      feature: t('compare.row.multiLocation', lang),
      ours: true,
      spreadsheet: false,
      contractor: true,
    },
    {
      feature: t('compare.row.reports', lang),
      ours: true,
      spreadsheet: false,
      contractor: 'partial',
    },
    {
      feature: t('compare.row.mobile', lang),
      ours: true,
      spreadsheet: false,
      contractor: true,
    },
    {
      feature: t('compare.row.setup', lang),
      ours: true,
      spreadsheet: 'partial',
      contractor: false,
    },
    {
      feature: t('compare.row.pricing', lang),
      ours: true,
      spreadsheet: true,
      contractor: false,
    },
  ];

  return (
    <section className="section" id="comparison">
      <div className="container">
        <div className="text-center">
          <span className="section-tag">{t('compare.tag', lang)}</span>
          <h2 className="section-title">{t('compare.title', lang)}</h2>
          <p className="section-subtitle">{t('compare.sub', lang)}</p>
        </div>
        <div className="comparison-wrap">
          <table className="comparison-table" role="table">
            <thead className="comparison-header">
              <tr>
                <th scope="col" />
                <th scope="col" className="col-ours">
                  <span className="ours-badge">
                    {t('compare.col.firetrack', lang)}
                  </span>
                </th>
                <th scope="col">{t('compare.col.spreadsheets', lang)}</th>
                <th scope="col">{t('compare.col.contractor', lang)}</th>
              </tr>
            </thead>
            <tbody className="comparison-body">
              {rows.map(row => (
                <tr key={row.feature}>
                  <td>{row.feature}</td>
                  <td>
                    <Cell val={row.ours} />
                  </td>
                  <td>
                    <Cell val={row.spreadsheet} />
                  </td>
                  <td>
                    <Cell val={row.contractor} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  {
    stars: '★★★★★',
    quote:
      '"We manage 47 buildings across 3 states. Before Look2it we had three different spreadsheets and still failed our annual audit. Now I can see compliance status for every location in real-time — it\'s completely changed how our team operates."',
    name: 'Sarah Chen',
    role: 'Head of Facilities, Meridian Properties',
    initials: 'SC',
  },
  {
    stars: '★★★★★',
    quote:
      '"The automated reminders alone saved us from two potential violations last quarter. When I showed our insurance provider the compliance reports, they actually reduced our premium. Look2it paid for itself in month one."',
    name: 'James Okafor',
    role: 'HSE Manager, Clearview Manufacturing',
    initials: 'JO',
  },
  {
    stars: '★★★★★',
    quote:
      '"Setup took less than a morning. We imported our asset list, set inspection schedules, and Look2it handled the rest. The mobile app means our maintenance team actually uses it — that\'s the real win."',
    name: 'Rachel Torres',
    role: 'VP Operations, Sunrise Hospitality Group',
    initials: 'RT',
  },
];

function Testimonials() {
  const { lang } = useLang();

  return (
    <section className="section section--alt" id="customers">
      <div className="container">
        <div className="text-center">
          <span className="section-tag">{t('testimonials.tag', lang)}</span>
          <h2 className="section-title">{t('testimonials.title', lang)}</h2>
        </div>
        <div className="testimonials__grid">
          {TESTIMONIALS.map(item => (
            <Card key={item.name} className="testimonial-card">
              <div className="testimonial-stars" aria-label="5 out of 5 stars">
                {item.stars}
              </div>
              <p className="testimonial-quote">{item.quote}</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar" aria-hidden="true">
                  {item.initials}
                </div>
                <div>
                  <div className="testimonial-name">{item.name}</div>
                  <div className="testimonial-role">{item.role}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const { lang } = useLang();

  const plans = [
    {
      tier: t('pricing.starter', lang),
      price: '$49',
      description: t('pricing.starter.desc', lang),
      features: [
        t('pricing.starter.f1', lang),
        t('pricing.starter.f2', lang),
        t('pricing.starter.f3', lang),
        t('pricing.starter.f4', lang),
        t('pricing.starter.f5', lang),
        t('pricing.starter.f6', lang),
      ],
      cta: t('pricing.startTrial', lang),
      href: '/signup',
      variant: 'outline' as const,
      featured: false,
    },
    {
      tier: t('pricing.growth', lang),
      price: '$149',
      description: t('pricing.growth.desc', lang),
      features: [
        t('pricing.growth.f1', lang),
        t('pricing.growth.f2', lang),
        t('pricing.growth.f3', lang),
        t('pricing.growth.f4', lang),
        t('pricing.growth.f5', lang),
        t('pricing.growth.f6', lang),
        t('pricing.growth.f7', lang),
      ],
      cta: t('pricing.startTrial', lang),
      href: '/signup',
      variant: 'brand' as const,
      featured: true,
    },
    {
      tier: t('pricing.enterprise', lang),
      price: t('pricing.custom', lang),
      description: t('pricing.enterprise.desc', lang),
      features: [
        t('pricing.enterprise.f1', lang),
        t('pricing.enterprise.f2', lang),
        t('pricing.enterprise.f3', lang),
        t('pricing.enterprise.f4', lang),
        t('pricing.enterprise.f5', lang),
        t('pricing.enterprise.f6', lang),
        t('pricing.enterprise.f7', lang),
      ],
      cta: t('pricing.contactSales', lang),
      href: '/contact',
      variant: 'outline' as const,
      featured: false,
    },
  ];

  return (
    <section className="section" id="pricing">
      <div className="container">
        <div className="text-center">
          <span className="section-tag">{t('pricing.tag', lang)}</span>
          <h2 className="section-title">{t('pricing.title', lang)}</h2>
          <p className="section-subtitle">{t('pricing.sub', lang)}</p>
        </div>
        <div className="pricing__grid">
          {plans.map(plan => (
            <div
              key={plan.tier}
              className={`pricing-card${plan.featured ? ' pricing-card--featured' : ''}`}
            >
              {plan.featured && (
                <div className="pricing-featured-badge">
                  {t('pricing.mostPopular', lang)}
                </div>
              )}
              <div className="pricing-tier">{plan.tier}</div>
              <div className="pricing-price">
                <span className="pricing-price__amount">{plan.price}</span>
                {plan.price.startsWith('$') && (
                  <span className="pricing-price__period">
                    {t('pricing.perMonth', lang)}
                  </span>
                )}
              </div>
              <p className="pricing-description">{plan.description}</p>
              <ul
                className="pricing-features"
                aria-label={`${plan.tier} features`}
              >
                {plan.features.map(f => (
                  <li key={f} className="pricing-feature">
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                as="a"
                href={plan.href}
                variant={plan.variant}
                size="md"
                className="pricing-cta"
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>
        <p className="pricing-note">{t('pricing.note', lang)}</p>
      </div>
    </section>
  );
}

function FAQ() {
  const { lang } = useLang();
  const [open, setOpen] = useState<number | null>(null);

  const faqs = [
    { q: t('faq.q1', lang), a: t('faq.a1', lang) },
    { q: t('faq.q2', lang), a: t('faq.a2', lang) },
    { q: t('faq.q3', lang), a: t('faq.a3', lang) },
    { q: t('faq.q4', lang), a: t('faq.a4', lang) },
    { q: t('faq.q5', lang), a: t('faq.a5', lang) },
  ];

  return (
    <section className="section section--alt" id="faq">
      <div className="container">
        <div className="text-center">
          <span className="section-tag">{t('faq.tag', lang)}</span>
          <h2 className="section-title">{t('faq.title', lang)}</h2>
        </div>
        <div className="faq__list" role="list">
          {faqs.map((item, i) => (
            <div key={i} className="faq-item" role="listitem">
              <button
                className={`faq-question${open === i ? ' faq-question--open' : ''}`}
                onClick={() => setOpen(open === i ? null : i)}
                aria-expanded={open === i}
                aria-controls={`faq-answer-${i}`}
              >
                {item.q}
                <span className="faq-chevron" aria-hidden="true">
                  ▼
                </span>
              </button>
              {open === i && (
                <div
                  id={`faq-answer-${i}`}
                  className="faq-answer"
                  role="region"
                >
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { lang } = useLang();

  return (
    <section className="section final-cta">
      <div className="container">
        <h2 className="final-cta__title">{t('cta.title', lang)}</h2>
        <p className="final-cta__sub">{t('cta.sub', lang)}</p>
        <div className="final-cta__actions">
          <Button as="a" href="/signup" variant="brand" size="lg">
            {t('hero.startTrial', lang)}
          </Button>
          <a href="/demo" className="btn btn--outline-white btn--lg">
            {t('cta.bookDemo', lang)}
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const { lang, setLang } = useLang();

  const FOOTER_COLS = [
    {
      title: t('footer.product', lang),
      links: [
        { label: t('footer.link.features', lang), href: '#features' },
        { label: t('footer.link.pricing', lang), href: '#pricing' },
        { label: t('footer.link.integrations', lang), href: '#' },
        { label: t('footer.link.changelog', lang), href: '#' },
        { label: t('footer.link.roadmap', lang), href: '#' },
      ],
    },
    {
      title: t('footer.resources', lang),
      links: [
        { label: t('footer.link.documentation', lang), href: '#' },
        { label: t('footer.link.blog', lang), href: '#' },
        { label: t('footer.link.complianceGuides', lang), href: '#' },
        { label: t('footer.link.apiReference', lang), href: '#' },
        { label: t('footer.link.status', lang), href: '#' },
      ],
    },
    {
      title: t('footer.company', lang),
      links: [
        { label: t('footer.link.about', lang), href: '#' },
        { label: t('footer.link.careers', lang), href: '#' },
        { label: t('footer.link.press', lang), href: '#' },
        { label: t('footer.link.contact', lang), href: '#' },
      ],
    },
    {
      title: t('footer.legal', lang),
      links: [
        { label: t('footer.link.privacy', lang), href: '#' },
        { label: t('footer.link.terms', lang), href: '#' },
        { label: t('footer.link.security', lang), href: '#' },
        { label: t('footer.link.gdpr', lang), href: '#' },
      ],
    },
  ];

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div className="footer__brand">
            <a href="/" className="footer__logo" aria-label="Look2it">
              <img
                src="/logo-wordmark-neg.svg"
                alt="Look2it"
                className="footer__logo-img"
              />
            </a>
            <p className="footer__tagline">{t('footer.tagline', lang)}</p>
          </div>
          {FOOTER_COLS.map(col => (
            <div key={col.title}>
              <div className="footer__col-title">{col.title}</div>
              <ul className="footer__links">
                {col.links.map(link => (
                  <li key={link.label}>
                    <a href={link.href} className="footer__link">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="footer__lang">
          <span className="footer__lang-label">
            {t('footer.language', lang)}:
          </span>
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              className={`footer__lang-btn${l.code === lang ? ' footer__lang-btn--active' : ''}`}
              onClick={() => setLang(l.code)}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="footer__bottom">
          <span>{t('footer.copyright', lang)}</span>
          <span>{t('footer.madeFor', lang)}</span>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <>
      <Navbar />
      <main>
        <Hero />
        <TrustBar />
        <PainPoints />
        <Features />
        <HowItWorks />
        <Comparison />
        <Testimonials />
        <Pricing />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </>
  );
}
